/**
 * SCOPEvision flow — AI plan reading with Claude Sonnet Vision.
 *
 * Sequence:
 *   1. Pre-upload gate — acceptance criteria + no-alterations tick + upload
 *   2. Spinner — "SCOPEvision is reading your plan…" (~7–10s)
 *   3a. Low confidence: blocked screen, steer to dimensions / draw route
 *   3b. High confidence: per-room confirmation list with editable m²
 *   4. User confirms → backend writes floors_map → navigate to /scope/draw
 *
 * NB: brand is "SCOPEvision". We do not say "AI" in user copy — disclosed in T&Cs.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, Ruler, FileImage, Pencil, Loader2, Sparkles, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AcceptanceCriteria() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 18 }}>
      <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 12, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#065F46', fontSize: 11, fontWeight: 800, letterSpacing: '.06em', marginBottom: 8 }}>
          <CheckCircle2 className="w-3.5 h-3.5" /> WORKS WELL
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, color: '#064E3B', fontSize: 13, lineHeight: 1.6 }}>
          <li>Estate agent / Zoopla floor plans</li>
          <li>Architect PDF or PNG with printed room labels</li>
          <li>Plans with a printed total area (e.g. "52.1 SQ.M.")</li>
        </ul>
      </div>
      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#991B1B', fontSize: 11, fontWeight: 800, letterSpacing: '.06em', marginBottom: 8 }}>
          <AlertTriangle className="w-3.5 h-3.5" /> USE ROOM DIMENSIONS INSTEAD
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, color: '#7F1D1D', fontSize: 13, lineHeight: 1.6 }}>
          <li>Hand-drawn sketches</li>
          <li>Phone photos of paper plans</li>
          <li>Plans without room labels or floor totals</li>
        </ul>
      </div>
    </div>
  );
}

function PreUploadGate({ onUpload, onUseDimensions, busy }) {
  const [noAlterations, setNoAlterations] = useState(false);
  const fileRef = useRef(null);

  const trigger = () => {
    if (!noAlterations || busy) return;
    fileRef.current?.click();
  };

  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onUpload(f, noAlterations);
    e.target.value = ''; // allow re-picking same file
  };

  return (
    <div data-testid="scopevision-preupload" style={{ maxWidth: 820, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderRadius: 999, background: '#EFF6FF', color: '#1D4ED8', fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 14 }}>
          <Sparkles className="w-3.5 h-3.5" /> SCOPEvision
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0F2742', margin: '0 0 10px', letterSpacing: '-0.015em' }}>
          Upload your plan and we'll identify the rooms
        </h1>
        <p style={{ fontSize: 14, color: '#475569', margin: 0, lineHeight: 1.6, maxWidth: 600, marginInline: 'auto' }}>
          Works best on estate agent and architect floor plans with printed labels and a total area. ~10 seconds.
        </p>
      </div>

      <AcceptanceCriteria />

      <div style={{ background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 14, padding: '20px 24px' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', marginBottom: 18 }} data-testid="scopevision-no-alterations-label">
          <input
            type="checkbox"
            checked={noAlterations}
            onChange={(e) => setNoAlterations(e.target.checked)}
            data-testid="scopevision-no-alterations"
            style={{ marginTop: 3, width: 16, height: 16 }}
          />
          <span style={{ fontSize: 13, color: '#334155', lineHeight: 1.55 }}>
            <strong style={{ color: '#0F2742' }}>I confirm I'm not making structural changes or moving walls</strong> in this project. SCOPEvision prices the existing layout — for knock-throughs, extensions, or moving kitchens/bathrooms, please use <button onClick={onUseDimensions} style={{ background: 'none', border: 'none', color: '#2563EB', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 13 }} data-testid="scopevision-switch-dimensions-inline">the room-dimensions route</button> which supports those changes directly.
          </span>
        </label>

        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,application/pdf" onChange={onChange} style={{ display: 'none' }} data-testid="scopevision-file-input" />

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={trigger}
            disabled={!noAlterations || busy}
            data-testid="scopevision-upload-btn"
            style={{
              background: (!noAlterations || busy) ? '#CBD5E1' : '#0F2742',
              color: '#fff', border: 'none',
              padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700,
              cursor: (!noAlterations || busy) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <FileImage className="w-4 h-4" /> Upload plan (PNG, JPG, PDF · max 20MB)
          </button>
          <button
            onClick={onUseDimensions}
            data-testid="scopevision-switch-dimensions"
            style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Ruler className="w-4 h-4" /> Got a hand sketch? Use room dimensions instead →
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div data-testid="scopevision-loading" style={{ background: 'linear-gradient(135deg, #0F2742 0%, #1E3A8A 100%)', color: '#fff', borderRadius: 16, padding: '56px 32px', textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
      <Loader2 className="w-8 h-8" style={{ animation: 'spin 1s linear infinite', marginInline: 'auto', marginBottom: 16, display: 'block' }} />
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, letterSpacing: '-0.01em' }}>
        SCOPEvision is reading your plan…
      </div>
      <div style={{ fontSize: 13, opacity: 0.75, lineHeight: 1.55 }}>
        Identifying rooms, reading printed labels, estimating floor areas. This usually takes 7–15 seconds.
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function BlockedScreen({ reason, onUseDimensions, onDrawManually, onRetry }) {
  return (
    <div data-testid="scopevision-blocked" style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 14, padding: '24px 26px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, color: '#854D0E' }}>
          <AlertTriangle className="w-5 h-5" />
          <div style={{ fontSize: 15, fontWeight: 800 }}>SCOPEvision couldn't read this plan confidently</div>
        </div>
        <p style={{ fontSize: 14, color: '#713F12', lineHeight: 1.6, margin: 0 }}>
          {reason || 'The plan was unclear — too few labels, no printed floor total, or the image quality was low.'}
        </p>
        <p style={{ fontSize: 13, color: '#713F12', lineHeight: 1.6, marginTop: 10, fontStyle: 'italic' }}>
          You can still get an accurate SCOPE — pick one of the options below. Once you're done, you can book a Quantity Surveyor call if you'd like your numbers verified.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14 }}>
        <button
          onClick={onUseDimensions}
          data-testid="scopevision-blocked-dimensions"
          style={{ background: '#0F2742', color: '#fff', border: 'none', padding: '14px 18px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <Ruler className="w-4 h-4" /> Enter room dimensions
        </button>
        <button
          onClick={onDrawManually}
          data-testid="scopevision-blocked-draw"
          style={{ background: '#fff', color: '#0F2742', border: '1.5px solid #0F2742', padding: '14px 18px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <Pencil className="w-4 h-4" /> Draw the plan myself
        </button>
      </div>

      <div style={{ textAlign: 'center', marginTop: 18 }}>
        <button onClick={onRetry} data-testid="scopevision-blocked-retry" style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
          Try another plan file
        </button>
      </div>
    </div>
  );
}

function ConfirmationScreen({ result, onBack, onAccept, submitting }) {
  const [floors, setFloors] = useState(
    (result.floors || []).map((f) => ({
      ...f,
      rooms: (f.rooms || []).map((r) => ({ ...r })),
    }))
  );

  const updateRoomArea = (fIdx, rIdx, value) => {
    setFloors((prev) => {
      const copy = prev.map((f, i) => i === fIdx ? { ...f, rooms: f.rooms.map((r, j) => j === rIdx ? { ...r, approx_area_m2: Number(value) || 0 } : r) } : f);
      return copy;
    });
  };

  const removeRoom = (fIdx, rIdx) => {
    setFloors((prev) => prev.map((f, i) => i === fIdx ? { ...f, rooms: f.rooms.filter((_, j) => j !== rIdx) } : f));
  };

  const addRoom = (fIdx) => {
    setFloors((prev) => prev.map((f, i) => i === fIdx ? {
      ...f,
      rooms: [...f.rooms, { name: 'New room', type: 'other', approx_area_m2: 8, confidence: 'medium' }]
    } : f));
  };

  const updateRoomName = (fIdx, rIdx, value) => {
    setFloors((prev) => prev.map((f, i) => i === fIdx ? { ...f, rooms: f.rooms.map((r, j) => j === rIdx ? { ...r, name: value } : r) } : f));
  };

  const totalRooms = floors.reduce((sum, f) => sum + (f.rooms?.length || 0), 0);

  return (
    <div data-testid="scopevision-confirm" style={{ maxWidth: 820, margin: '0 auto' }}>
      <button onClick={onBack} data-testid="scopevision-confirm-back" style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
        <ArrowLeft className="w-4 h-4" /> Upload a different plan
      </button>

      <div style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', border: '1px solid #A7F3D0', borderRadius: 14, padding: '18px 22px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#065F46', fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
          <CheckCircle2 className="w-4 h-4" /> SCOPEvision found {totalRooms} room{totalRooms === 1 ? '' : 's'}
        </div>
        <div style={{ fontSize: 14, color: '#064E3B', lineHeight: 1.55 }}>
          Check the list below and correct anything. You can edit room names, tweak floor areas, or remove a room. Your Schedule of Works will be built from these rooms.
        </div>
        {result.notes_for_user && (
          <div style={{ fontSize: 12, color: '#065F46', marginTop: 8, fontStyle: 'italic' }}>
            {result.notes_for_user}
          </div>
        )}
      </div>

      {floors.map((f, fIdx) => (
        <div key={fIdx} data-testid={`scopevision-floor-${fIdx}`} style={{ background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 14, padding: '18px 22px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #F1F5F9' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0F2742' }}>{f.label}</div>
            <div style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>Total: {f.total_area_m2} m²</div>
          </div>
          {f.rooms.map((r, rIdx) => (
            <div key={rIdx} data-testid={`scopevision-room-${fIdx}-${rIdx}`} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 30px', gap: 10, alignItems: 'center', padding: '10px 0', borderBottom: rIdx < f.rooms.length - 1 ? '1px solid #F8FAFC' : 'none' }}>
              <input
                type="text"
                value={r.name}
                onChange={(e) => updateRoomName(fIdx, rIdx, e.target.value)}
                data-testid={`scopevision-room-name-${fIdx}-${rIdx}`}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: 13, fontWeight: 600, color: '#0F2742' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  value={r.approx_area_m2}
                  onChange={(e) => updateRoomArea(fIdx, rIdx, e.target.value)}
                  data-testid={`scopevision-room-area-${fIdx}-${rIdx}`}
                  style={{ width: 80, padding: '8px 10px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: 13, textAlign: 'right' }}
                />
                <span style={{ fontSize: 12, color: '#64748B' }}>m²</span>
              </div>
              <button
                onClick={() => removeRoom(fIdx, rIdx)}
                data-testid={`scopevision-room-remove-${fIdx}-${rIdx}`}
                style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', padding: 6 }}
                title="Remove room"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() => addRoom(fIdx)}
            data-testid={`scopevision-add-room-${fIdx}`}
            style={{ marginTop: 10, background: 'none', border: '1.5px dashed #CBD5E1', color: '#2563EB', fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            + Add a missing room
          </button>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
        <button
          onClick={() => onAccept(floors)}
          disabled={submitting || totalRooms === 0}
          data-testid="scopevision-accept-btn"
          style={{ background: (submitting || totalRooms === 0) ? '#94A3B8' : '#0F2742', color: '#fff', border: 'none', padding: '13px 26px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: (submitting || totalRooms === 0) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          {submitting ? 'Creating your project…' : 'Create my project with these rooms'}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function SCOPEVisionFlow({ draftId, onBack, onUseDimensions, onComplete }) {
  const [phase, setPhase] = useState('upload');  // 'upload' | 'loading' | 'blocked' | 'confirm'
  const [result, setResult] = useState(null);
  const [blockedReason, setBlockedReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleUpload = async (file, noAlterations) => {
    if (!draftId) return;
    setPhase('loading');
    const form = new FormData();
    form.append('file', file);
    form.append('no_alterations', noAlterations ? 'true' : 'false');
    try {
      const res = await axios.post(`${API}/scope/drafts/${draftId}/vision-detect`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data || {};
      if (data.blocked) {
        setBlockedReason(data.reason || 'SCOPEvision could not read this plan.');
        setPhase('blocked');
      } else {
        setResult(data);
        setPhase('confirm');
      }
    } catch (err) {
      setBlockedReason(err.response?.data?.error || err.response?.data?.reason || 'SCOPEvision is temporarily unavailable.');
      setPhase('blocked');
    }
  };

  const handleAccept = async (floors) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/scope/drafts/${draftId}/vision-accept`, { floors });
      onComplete?.();
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="scopevision-flow">
      <button onClick={onBack} data-testid="scopevision-back" style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18 }}>
        <ArrowLeft className="w-4 h-4" /> Choose a different method
      </button>

      {phase === 'upload' && (
        <PreUploadGate onUpload={handleUpload} onUseDimensions={onUseDimensions} busy={false} />
      )}
      {phase === 'loading' && <LoadingScreen />}
      {phase === 'blocked' && (
        <BlockedScreen
          reason={blockedReason}
          onUseDimensions={onUseDimensions}
          onDrawManually={onBack}
          onRetry={() => setPhase('upload')}
        />
      )}
      {phase === 'confirm' && result && (
        <ConfirmationScreen
          result={result}
          onBack={() => setPhase('upload')}
          onAccept={handleAccept}
          submitting={submitting}
        />
      )}
    </div>
  );
}
